import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  RefreshCw,
  X,
  Phone,
  Signal,
  MessageSquare,
  Building2,
  Power,
  AlertTriangle,
  Activity,
  RotateCcw,
  ChevronRight,
  Cpu,
  HardDrive,
  Zap,
  Inbox,
  UserPlus,
  Clock,
  Check,
  Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SIMVaultMonitor = () => {
  const { isDarkMode } = useOutletContext() || { isDarkMode: true };
  const [devices, setDevices] = useState([]);
  const [portAssignments, setPortAssignments] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPort, setSelectedPort] = useState(null);
  const [selectedPortIndex, setSelectedPortIndex] = useState(null);
  const [systemStatus, setSystemStatus] = useState({
    power: true,
    system: true,
    alarm: false
  });
  
  // Unassigned SMS Queue state
  const [unassignedSms, setUnassignedSms] = useState([]);
  const [showSmsQueue, setShowSmsQueue] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [devicesRes, statsRes, assignmentsRes, unassignedRes, merchantsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/simvault/devices`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/simvault/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/simvault/port-assignments`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/sms/unassigned`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/merchants`, { credentials: 'include' })
      ]);

      if (devicesRes.ok) {
        const devicesData = await devicesRes.json();
        setDevices(devicesData);
        // Check for any errors to set alarm status
        const hasErrors = devicesData.some(d => d.status === 'error');
        setSystemStatus(prev => ({ ...prev, alarm: hasErrors }));
      }
      if (statsRes.ok) setStats(await statsRes.json());
      
      // Process port assignments into a map
      if (assignmentsRes.ok) {
        const assignmentsData = await assignmentsRes.json();
        const assignmentMap = {};
        assignmentsData.forEach(a => {
          if (a.device_id) {
            assignmentMap[a.device_id] = a;
          }
        });
        setPortAssignments(assignmentMap);
      }
      
      // Unassigned SMS queue
      if (unassignedRes.ok) {
        const unassignedData = await unassignedRes.json();
        setUnassignedSms(unassignedData);
      }
      
      // Merchants list
      if (merchantsRes.ok) {
        const merchantsData = await merchantsRes.json();
        setMerchants(merchantsData.merchants || merchantsData || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setSystemStatus(prev => ({ ...prev, alarm: true }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleRestartPort = async (deviceId) => {
    try {
      await fetch(`${API_URL}/api/admin/simvault/devices/${deviceId}/restart`, {
        method: 'POST',
        credentials: 'include'
      });
      fetchData();
    } catch (error) {
      console.error('Error restarting port:', error);
    }
  };

  // Assign SMS to a merchant
  const handleAssignSms = async (smsId) => {
    const merchantId = selectedMerchant[smsId];
    if (!merchantId) {
      toast.error('Please select a merchant');
      return;
    }
    
    setAssigningId(smsId);
    try {
      const response = await fetch(`${API_URL}/api/admin/sms/${smsId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ merchant_id: merchantId })
      });
      
      if (response.ok) {
        toast.success('SMS assigned to merchant successfully');
        // Remove from unassigned list
        setUnassignedSms(prev => prev.filter(sms => sms.sms_id !== smsId));
        // Clear selection
        setSelectedMerchant(prev => {
          const updated = { ...prev };
          delete updated[smsId];
          return updated;
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign SMS');
      }
    } catch (error) {
      console.error('Error assigning SMS:', error);
      toast.error('Failed to assign SMS');
    } finally {
      setAssigningId(null);
    }
  };

  // Dismiss SMS (mark as ignored)
  const handleDismissSms = async (smsId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sms/${smsId}/dismiss`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('SMS dismissed');
        setUnassignedSms(prev => prev.filter(sms => sms.sms_id !== smsId));
      }
    } catch (error) {
      console.error('Error dismissing SMS:', error);
    }
  };

  const getPortColor = (status) => {
    switch (status) {
      case 'active':
      case 'online':
        return 'bg-emerald-500 shadow-emerald-500/50';
      case 'sending':
      case 'busy':
        return 'bg-amber-400 shadow-amber-400/50 animate-pulse';
      case 'error':
        return 'bg-red-500 shadow-red-500/50';
      default:
        return 'bg-slate-600 shadow-slate-600/30';
    }
  };

  const getPortBorderColor = (status) => {
    switch (status) {
      case 'active':
      case 'online':
        return 'border-emerald-400';
      case 'sending':
      case 'busy':
        return 'border-amber-400';
      case 'error':
        return 'border-red-400';
      default:
        return 'border-slate-500';
    }
  };

  const getLEDColor = (isOn, type) => {
    if (!isOn) return 'bg-slate-700';
    switch (type) {
      case 'power': return 'bg-emerald-500 shadow-lg shadow-emerald-500/50';
      case 'system': return 'bg-blue-500 shadow-lg shadow-blue-500/50 animate-pulse';
      case 'alarm': return 'bg-red-500 shadow-lg shadow-red-500/50 animate-pulse';
      default: return 'bg-slate-500';
    }
  };

  // Generate 80 ports for a full rack-mounted switch
  const generateMockPort = (index, realDeviceData = null) => {
    const statuses = ['active', 'active', 'active', 'sending', 'active', 'online', 'active', 'active', 'error', 'offline', 'active', 'active'];
    const carriers = ['MTN', 'Orange', 'MTN', 'Orange', 'MTN', 'Orange', 'MTN', 'Orange', 'MTN', 'Orange', 'MTN', 'Orange'];
    const defaultMerchants = [
      'TechStore LR', 'QuickMart', 'Orange Money', 'MTN Hub', 'PayFast', 'CashFlow', 
      'MarketPay', 'SwiftPay', 'MobiCash', 'EasyPay', 'FastMoney', 'PayNow',
      'QuickCash', 'MoneyFlow', 'CashExpress', 'PayWave', null, null, 
      'SmartPay', 'FlexPay', 'RapidPay', 'InstaPay', 'GoPay', 'ZapPay',
      null, null, null, null, 'MegaPay', 'UltraPay',
      'TopUp LR', 'CreditNow', null, null, 'BankLink', 'DirectPay',
      'SafePay', 'SecurePay', null, null, 'TrustPay', 'ReliPay',
      null, null, null, null, 'NextPay', 'FuturePay',
      'AlphaPay', 'BetaPay', null, null, 'GammaPay', 'DeltaPay',
      'OmegaPay', 'SigmaPay', null, null, 'ThetaPay', 'ZetaPay',
      null, null, null, null, 'KappaPay', 'LambdaPay',
      'MuPay', 'NuPay', null, null, 'XiPay', 'PiPay',
      null, null, null, null, 'RhoPay', 'TauPay'
    ];
    
    // If real device data exists, use it
    if (realDeviceData) {
      return {
        device_id: realDeviceData.device_id || `sim_${index + 1}`,
        phone_number: realDeviceData.phone_number,
        carrier: realDeviceData.carrier || carriers[index % carriers.length],
        status: realDeviceData.status || 'active',
        signal_strength: realDeviceData.signal_strength || Math.floor(Math.random() * 40) + 60,
        sms_today: realDeviceData.sms_today || Math.floor(Math.random() * 100),
        merchant_name: realDeviceData.merchant_name || realDeviceData.merchant?.business_name || null,
        last_activity: realDeviceData.last_activity || realDeviceData.last_seen_at
      };
    }
    
    const status = index < 60 ? statuses[index % statuses.length] : (index < 70 ? 'offline' : 'offline');
    const hasActivity = status !== 'offline';
    
    return {
      device_id: `sim_${index + 1}`,
      phone_number: hasActivity ? `+231${880000000 + index}` : null,
      carrier: hasActivity ? carriers[index % carriers.length] : null,
      status: status,
      signal_strength: hasActivity ? Math.floor(Math.random() * 40) + 60 : 0,
      sms_today: hasActivity ? Math.floor(Math.random() * 100) : 0,
      merchant_name: defaultMerchants[index] || null,
      last_activity: hasActivity ? new Date(Date.now() - Math.random() * 3600000).toISOString() : null
    };
  };

  // Create a map of real devices by device_id
  const deviceMap = {};
  devices.forEach(d => {
    if (d.device_id) {
      deviceMap[d.device_id] = d;
    }
  });

  // Always show 160 ports (2 switches × 80 ports) - merge real devices with mock ports
  const totalPorts = 160;
  const displayDevices = Array.from({ length: totalPorts }, (_, i) => {
    const deviceId = `sim_${i + 1}`;
    const realDevice = deviceMap[deviceId];
    const portAssignment = portAssignments[deviceId];
    
    // Merge port assignment data with device data
    const mergedDevice = realDevice ? { ...realDevice } : null;
    if (mergedDevice && portAssignment) {
      mergedDevice.merchant_name = portAssignment.merchant_name || mergedDevice.merchant_name;
      mergedDevice.merchant_id = portAssignment.merchant_id || mergedDevice.merchant_id;
    }
    
    const mockData = generateMockPort(i, mergedDevice);
    
    // Override merchant_name from port assignment if exists
    if (portAssignment && portAssignment.merchant_name) {
      mockData.merchant_name = portAssignment.merchant_name;
    }
    
    return mockData;
  });

  // 20 ports per row (Row 1: 1-20, Row 2: 21-40, Row 3: 41-60, Row 4: 61-80)
  const portsPerRow = 20;
  const portsPerSwitch = 80;
  
  // Split into two switches
  const switch1Devices = displayDevices.slice(0, portsPerSwitch);
  const switch2Devices = displayDevices.slice(portsPerSwitch, portsPerSwitch * 2);
  
  // Create rows for each switch
  const createRows = (devices) => {
    const rows = [];
    for (let i = 0; i < devices.length; i += portsPerRow) {
      rows.push(devices.slice(i, i + portsPerRow));
    }
    return rows;
  };
  
  const switch1Rows = createRows(switch1Devices);
  const switch2Rows = createRows(switch2Devices);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-slate-900">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4" data-testid="simvault-monitor">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <HardDrive className="w-8 h-8 text-teal-500" />
            <div>
              <h1 className="text-xl font-bold text-white tracking-wide">
                SIMVault Network Switch
              </h1>
              <p className="text-xs text-slate-500 font-mono">
                MODEL: SVN-80G × 2 | 160-PORT STACK | FIRMWARE: v3.2.0
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Unassigned SMS Queue Button */}
          <button
            onClick={() => setShowSmsQueue(!showSmsQueue)}
            className={`flex items-center gap-2 px-4 py-2 rounded border transition-all ${
              showSmsQueue 
                ? 'bg-amber-600 hover:bg-amber-500 text-white border-amber-500' 
                : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'
            }`}
            data-testid="sms-queue-btn"
          >
            <Inbox className="w-4 h-4" />
            <span className="font-mono text-sm">SMS QUEUE</span>
            {unassignedSms.length > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {unassignedSms.length}
              </span>
            )}
          </button>
          
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition-all"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="font-mono text-sm">REFRESH</span>
          </button>
        </div>
      </div>

      {/* System Metrics */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-xs font-mono uppercase">Devices Online</span>
            <Cpu className="w-4 h-4 text-emerald-500" />
          </div>
          <p className="text-xl font-bold text-emerald-400 font-mono mt-1">
            {stats?.online_devices || displayDevices.filter(d => d.status !== 'offline').length}
            <span className="text-slate-600 text-sm">/{displayDevices.length}</span>
          </p>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-xs font-mono uppercase">SMS Today</span>
            <MessageSquare className="w-4 h-4 text-blue-500" />
          </div>
          <p className="text-xl font-bold text-blue-400 font-mono mt-1">
            {stats?.total_sms_today || displayDevices.reduce((sum, d) => sum + (d.sms_today || 0), 0)}
          </p>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-xs font-mono uppercase">Routed Today</span>
            <Activity className="w-4 h-4 text-purple-500" />
          </div>
          <p className="text-xl font-bold text-purple-400 font-mono mt-1">
            {stats?.routed_today || Math.floor((stats?.total_sms_today || 0) * 0.85)}
          </p>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-xs font-mono uppercase">Queue</span>
            <Zap className="w-4 h-4 text-amber-500" />
          </div>
          <p className="text-xl font-bold text-amber-400 font-mono mt-1">
            {stats?.queue_size || 0}
          </p>
        </div>
      </div>

      {/* Network Switch Panel 1 */}
      <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-xl border-2 border-slate-700 shadow-2xl overflow-hidden mb-6">
        {/* Switch Top Edge - Metal Look */}
        <div className="h-2 bg-gradient-to-b from-slate-600 to-slate-700"></div>
        
        {/* Switch Face */}
        <div className="p-4">
          <div className="flex gap-6">
            {/* Left Status LEDs Panel */}
            <div className="flex flex-col items-center gap-4 pr-6 border-r border-slate-700">
              <div className="text-xs font-mono text-slate-500 mb-2">SWITCH 1</div>
              
              {/* PWR LED */}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${getLEDColor(systemStatus.power, 'power')}`}></div>
                <span className="text-[10px] font-mono text-slate-400">PWR</span>
              </div>
              
              {/* SYS LED */}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${getLEDColor(systemStatus.system, 'system')}`}></div>
                <span className="text-[10px] font-mono text-slate-400">SYS</span>
              </div>
              
              {/* ALM LED */}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${getLEDColor(systemStatus.alarm, 'alarm')}`}></div>
                <span className="text-[10px] font-mono text-slate-400">ALM</span>
              </div>
            </div>

            {/* Port Grid */}
            <div className="flex-1">
              {switch1Rows.map((row, rowIndex) => (
                <div key={rowIndex} className="mb-4 last:mb-0">
                  {/* Port Numbers */}
                  <div className="flex gap-1.5 mb-0.5 pl-0.5">
                    {row.map((device, portIndex) => (
                      <div 
                        key={`num-${portIndex}`}
                        className="w-11 text-center text-[10px] font-mono text-slate-400"
                      >
                        {rowIndex * portsPerRow + portIndex + 1}
                      </div>
                    ))}
                  </div>
                  
                  {/* Port Row */}
                  <div className="flex gap-1.5">
                    {row.map((device, portIndex) => {
                      const globalIndex = rowIndex * portsPerRow + portIndex;
                      return (
                        <button
                          key={device.device_id}
                          onClick={() => {
                            setSelectedPort(device);
                            setSelectedPortIndex(globalIndex);
                          }}
                          className={`
                            w-11 h-11 rounded-lg border-2 transition-all duration-200
                            hover:scale-105 hover:brightness-110 cursor-pointer
                            flex items-center justify-center relative
                            ${getPortBorderColor(device.status)}
                            bg-slate-800 hover:bg-slate-750
                          `}
                          data-testid={`port-${globalIndex + 1}`}
                        >
                          {/* Port LED Indicator */}
                          <div className={`
                            w-4 h-4 rounded-full shadow-lg
                            ${getPortColor(device.status)}
                          `}></div>
                          
                          {/* Activity Indicator */}
                          {device.status === 'sending' && (
                            <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping"></div>
                          )}
                        </button>
                      );
                    })}
                    
                    {/* Fill empty slots if row is not complete */}
                    {row.length < portsPerRow && Array.from({ length: portsPerRow - row.length }).map((_, i) => (
                      <div 
                        key={`empty-${i}`}
                        className="w-11 h-11 rounded-lg border-2 border-dashed border-slate-700 bg-slate-800/50"
                      ></div>
                    ))}
                  </div>
                  
                  {/* Carrier Labels */}
                  <div className="flex gap-1.5 mt-0.5 pl-0.5">
                    {row.map((device, portIndex) => (
                      <div 
                        key={`carrier-${portIndex}`}
                        className={`w-11 text-center text-[8px] font-mono font-bold ${
                          device.carrier === 'MTN' ? 'text-yellow-500' : 
                          device.carrier === 'Orange' ? 'text-orange-500' : 'text-slate-600'
                        }`}
                      >
                        {device.carrier || '---'}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Right Panel - Model Info */}
            <div className="flex flex-col items-center gap-2 pl-6 border-l border-slate-700">
              <div className="text-xs font-mono text-slate-500 mb-2">INFO</div>
              <div className="w-16 h-16 rounded bg-slate-700 flex items-center justify-center">
                <HardDrive className="w-8 h-8 text-slate-500" />
              </div>
              <div className="text-[10px] font-mono text-slate-500 text-center">
                <div>SVN-80G</div>
                <div>1-80</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Switch Bottom Edge - Ventilation Slots */}
        <div className="h-4 bg-slate-900 flex items-center justify-center gap-1 border-t border-slate-700">
          {Array.from({ length: 80 }).map((_, i) => (
            <div key={i} className="w-1 h-2 bg-slate-700 rounded-sm"></div>
          ))}
        </div>
      </div>

      {/* Network Switch Panel 2 */}
      <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-xl border-2 border-slate-700 shadow-2xl overflow-hidden">
        {/* Switch Top Edge - Metal Look */}
        <div className="h-2 bg-gradient-to-b from-slate-600 to-slate-700"></div>
        
        {/* Switch Face */}
        <div className="p-4">
          <div className="flex gap-6">
            {/* Left Status LEDs Panel */}
            <div className="flex flex-col items-center gap-4 pr-6 border-r border-slate-700">
              <div className="text-xs font-mono text-slate-500 mb-2">SWITCH 2</div>
              
              {/* PWR LED */}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${getLEDColor(systemStatus.power, 'power')}`}></div>
                <span className="text-[10px] font-mono text-slate-400">PWR</span>
              </div>
              
              {/* SYS LED */}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${getLEDColor(systemStatus.system, 'system')}`}></div>
                <span className="text-[10px] font-mono text-slate-400">SYS</span>
              </div>
              
              {/* ALM LED */}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${getLEDColor(systemStatus.alarm, 'alarm')}`}></div>
                <span className="text-[10px] font-mono text-slate-400">ALM</span>
              </div>
            </div>

            {/* Port Grid */}
            <div className="flex-1">
              {switch2Rows.map((row, rowIndex) => (
                <div key={rowIndex} className="mb-4 last:mb-0">
                  {/* Port Numbers */}
                  <div className="flex gap-1.5 mb-0.5 pl-0.5">
                    {row.map((device, portIndex) => (
                      <div 
                        key={`num-${portIndex}`}
                        className="w-11 text-center text-[10px] font-mono text-slate-400"
                      >
                        {portsPerSwitch + rowIndex * portsPerRow + portIndex + 1}
                      </div>
                    ))}
                  </div>
                  
                  {/* Port Row */}
                  <div className="flex gap-1.5">
                    {row.map((device, portIndex) => {
                      const globalIndex = portsPerSwitch + rowIndex * portsPerRow + portIndex;
                      return (
                        <button
                          key={device.device_id}
                          onClick={() => {
                            setSelectedPort(device);
                            setSelectedPortIndex(globalIndex);
                          }}
                          className={`
                            w-11 h-11 rounded-lg border-2 transition-all duration-200
                            hover:scale-105 hover:brightness-110 cursor-pointer
                            flex items-center justify-center relative
                            ${getPortBorderColor(device.status)}
                            bg-slate-800 hover:bg-slate-750
                          `}
                          data-testid={`port-${globalIndex + 1}`}
                        >
                          {/* Port LED Indicator */}
                          <div className={`
                            w-4 h-4 rounded-full shadow-lg
                            ${getPortColor(device.status)}
                          `}></div>
                          
                          {/* Activity Indicator */}
                          {device.status === 'sending' && (
                            <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping"></div>
                          )}
                        </button>
                      );
                    })}
                    
                    {/* Fill empty slots if row is not complete */}
                    {row.length < portsPerRow && Array.from({ length: portsPerRow - row.length }).map((_, i) => (
                      <div 
                        key={`empty-${i}`}
                        className="w-11 h-11 rounded-lg border-2 border-dashed border-slate-700 bg-slate-800/50"
                      ></div>
                    ))}
                  </div>
                  
                  {/* Carrier Labels */}
                  <div className="flex gap-1.5 mt-0.5 pl-0.5">
                    {row.map((device, portIndex) => (
                      <div 
                        key={`carrier-${portIndex}`}
                        className={`w-11 text-center text-[8px] font-mono font-bold ${
                          device.carrier === 'MTN' ? 'text-yellow-500' : 
                          device.carrier === 'Orange' ? 'text-orange-500' : 'text-slate-600'
                        }`}
                      >
                        {device.carrier || '---'}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Right Panel - Model Info */}
            <div className="flex flex-col items-center gap-2 pl-6 border-l border-slate-700">
              <div className="text-xs font-mono text-slate-500 mb-2">INFO</div>
              <div className="w-16 h-16 rounded bg-slate-700 flex items-center justify-center">
                <HardDrive className="w-8 h-8 text-slate-500" />
              </div>
              <div className="text-[10px] font-mono text-slate-500 text-center">
                <div>SVN-80G</div>
                <div>81-160</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Switch Bottom Edge - Ventilation Slots */}
        <div className="h-4 bg-slate-900 flex items-center justify-center gap-1 border-t border-slate-700">
          {Array.from({ length: 80 }).map((_, i) => (
            <div key={i} className="w-1 h-2 bg-slate-700 rounded-sm"></div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center gap-8">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/50"></div>
          <span className="text-xs font-mono text-slate-400">Active</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-400 shadow-lg shadow-amber-400/50"></div>
          <span className="text-xs font-mono text-slate-400">Sending SMS</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/50"></div>
          <span className="text-xs font-mono text-slate-400">Error</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-slate-600"></div>
          <span className="text-xs font-mono text-slate-400">Offline</span>
        </div>
      </div>

      {/* Side Panel */}
      {selectedPort && (
        <div className="fixed inset-y-0 right-0 w-96 bg-slate-900 border-l border-slate-700 shadow-2xl z-50 transform transition-transform duration-300">
          {/* Panel Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800">
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full ${getPortColor(selectedPort.status)}`}></div>
              <h3 className="text-lg font-mono font-bold text-white">
                PORT {selectedPortIndex !== null ? selectedPortIndex + 1 : '?'}
              </h3>
            </div>
            <button
              onClick={() => {
                setSelectedPort(null);
                setSelectedPortIndex(null);
              }}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Panel Content */}
          <div className="p-6 space-y-6">
            {/* Phone Number */}
            <div className="bg-slate-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-mono mb-2">
                <Phone className="w-4 h-4" />
                PHONE NUMBER
              </div>
              <p className="text-xl font-mono font-bold text-white">
                {selectedPort.phone_number || 'Not Assigned'}
              </p>
            </div>

            {/* Carrier */}
            <div className="bg-slate-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-mono mb-2">
                <Signal className="w-4 h-4" />
                CARRIER
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xl font-mono font-bold ${
                  selectedPort.carrier === 'MTN' ? 'text-yellow-400' : 'text-orange-400'
                }`}>
                  {selectedPort.carrier || '---'}
                </span>
                {selectedPort.signal_strength && (
                  <span className="text-sm text-slate-400 font-mono">
                    {selectedPort.signal_strength}% signal
                  </span>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="bg-slate-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-mono mb-2">
                <Activity className="w-4 h-4" />
                STATUS
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${getPortColor(selectedPort.status)}`}></div>
                <span className={`text-lg font-mono font-bold uppercase ${
                  selectedPort.status === 'active' || selectedPort.status === 'online' ? 'text-emerald-400' :
                  selectedPort.status === 'sending' ? 'text-amber-400' :
                  selectedPort.status === 'error' ? 'text-red-400' :
                  'text-slate-400'
                }`}>
                  {selectedPort.status || 'Unknown'}
                </span>
              </div>
            </div>

            {/* SMS Sent Today */}
            <div className="bg-slate-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-mono mb-2">
                <MessageSquare className="w-4 h-4" />
                SMS SENT TODAY
              </div>
              <p className="text-2xl font-mono font-bold text-blue-400">
                {selectedPort.sms_today || 0}
              </p>
            </div>

            {/* Merchant Linked */}
            <div className="bg-slate-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-mono mb-2">
                <Building2 className="w-4 h-4" />
                MERCHANT LINKED
              </div>
              {selectedPort.merchant_name ? (
                <div className="flex items-center gap-2">
                  <span className="text-lg font-mono text-white">
                    {selectedPort.merchant_name}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </div>
              ) : (
                <span className="text-slate-500 font-mono">Not Linked</span>
              )}
            </div>

            {/* Last Activity */}
            <div className="text-xs font-mono text-slate-500 text-center">
              Last Activity: {selectedPort.last_activity ? 
                new Date(selectedPort.last_activity).toLocaleString() : 
                'Never'}
            </div>

            {/* Actions */}
            <div className="pt-4 border-t border-slate-700">
              <Button
                onClick={() => handleRestartPort(selectedPort.device_id)}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white font-mono"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                RESTART PORT
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay when side panel is open */}
      {selectedPort && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => {
            setSelectedPort(null);
            setSelectedPortIndex(null);
          }}
        ></div>
      )}

      {/* Unassigned SMS Queue Panel */}
      {showSmsQueue && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <Inbox className="w-6 h-6 text-amber-500" />
                <div>
                  <h2 className="text-lg font-bold text-white">Unassigned SMS Queue</h2>
                  <p className="text-xs text-slate-400">SMS messages that need manual routing</p>
                </div>
              </div>
              <button
                onClick={() => setShowSmsQueue(false)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            {/* SMS List */}
            <div className="overflow-y-auto max-h-[60vh]">
              {unassignedSms.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-slate-400">
                  <Check className="w-12 h-12 text-emerald-500 mb-3" />
                  <p className="text-lg font-medium text-white">All Clear!</p>
                  <p className="text-sm">No unassigned SMS messages in the queue</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {unassignedSms.map((sms) => (
                    <div key={sms.sms_id} className="p-4 hover:bg-slate-800/50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        {/* SMS Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              sms.network === 'MTN' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-orange-500/20 text-orange-400'
                            }`}>
                              {sms.network || 'Unknown'}
                            </span>
                            <span className="text-xs text-slate-500 font-mono">{sms.sender || 'Unknown Sender'}</span>
                            <span className="text-xs text-slate-600">•</span>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(sms.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-300 font-mono bg-slate-800/50 p-2 rounded border border-slate-700 line-clamp-2">
                            {sms.message || sms.raw_message || 'No message content'}
                          </p>
                          {sms.parsed && (
                            <div className="flex items-center gap-4 mt-2 text-xs">
                              {sms.parsed.amount && (
                                <span className="text-emerald-400">Amount: {sms.parsed.amount.toLocaleString()} LRD</span>
                              )}
                              {sms.parsed.reference && (
                                <span className="text-blue-400">Ref: {sms.parsed.reference}</span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Select 
                            value={selectedMerchant[sms.sms_id] || ''} 
                            onValueChange={(v) => setSelectedMerchant(prev => ({ ...prev, [sms.sms_id]: v }))}
                          >
                            <SelectTrigger className="w-48 bg-slate-800 border-slate-600 text-slate-200">
                              <SelectValue placeholder="Select Merchant" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-600">
                              {merchants.map((m) => (
                                <SelectItem 
                                  key={m.merchant_id} 
                                  value={m.merchant_id}
                                  className="text-slate-200 hover:bg-slate-700"
                                >
                                  {m.business_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <Button
                            size="sm"
                            onClick={() => handleAssignSms(sms.sms_id)}
                            disabled={!selectedMerchant[sms.sms_id] || assigningId === sms.sms_id}
                            className="bg-teal-600 hover:bg-teal-500"
                          >
                            {assigningId === sms.sms_id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserPlus className="w-4 h-4" />
                            )}
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDismissSms(sms.sms_id)}
                            className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-slate-700 bg-slate-800/50">
              <span className="text-sm text-slate-400">
                {unassignedSms.length} message{unassignedSms.length !== 1 ? 's' : ''} pending
              </span>
              <Button variant="outline" onClick={() => setShowSmsQueue(false)} className="border-slate-600">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SIMVaultMonitor;
